import { SvgIconProps, SxProps, Theme, alpha, buttonClasses } from '@mui/material'
import { C1Button, C1ButtonColorType, C1ButtonProps } from 'c1ui'
import { sxc } from 'components/no-ui/styles/styleTypes'
import React, { FC, MouseEventHandler } from 'react'

type C1IconButtonBaseProps = Omit<C1ButtonProps, 'variant' | 'color'> & {
    /** Icon size. Controls padding and passes `fontSize` to the child icon element. */
    size?: 'small' | 'medium';
    /** A single icon element (e.g. `<C1Icon icon={Pencil} />`). Gets cloned with the resolved `fontSize`. */
    children: React.ReactElement<SvgIconProps>;
    /** Required accessible label describing the action (e.g. "Edit rule", "Toggle sidebar"). */
    'aria-label': string;
    /** Click handler. */
    onClick?: MouseEventHandler<HTMLButtonElement>;
    /** Disables the button and applies grey styling. */
    disabled?: boolean;
    /** MUI system styles merged onto the root button. */
    sx?: SxProps<Theme>;
};

type OutlinedIconButtonProps = {
    /** Renders a bordered icon button. Required to access non-inherit colors. */
    variant: 'outlined';
    /** Color theme. All C1 button colors are available in outlined mode. */
    color?: C1ButtonColorType;
};

type TextIconButtonProps = {
    /** Renders a minimal icon button with no border. Default variant. Only `inherit` color is supported. */
    variant?: 'text';
    /** Only `inherit` is available for text variant. */
    color?: 'inherit';
};

export type C1IconButtonProps =
    & C1IconButtonBaseProps
    & (OutlinedIconButtonProps | TextIconButtonProps)

const overridesSx = {
    [`&.${buttonClasses.outlinedSuccess}`]: {
        '&:hover': {
            borderColor: (t: Theme): string => alpha(t.palette.success.dark, 0.5),
            color: 'success.dark',
        },
    },
    [`&.${buttonClasses.outlinedError}`]: {
        '&:hover': {
            borderColor: (t: Theme): string => alpha(t.palette.error.dark, 0.5),
            color: 'error.dark',
        },
    },
    [`&.${buttonClasses.textPrimary}`]: {
        color: 'text.secondary',
        '&:hover': {
            color: 'primary.main',
            backgroundColor: 'primary.extraLight',
        },
    },
    [`&.${buttonClasses.disabled}`]: {
        color: 'grey.400',
        backgroundColor: 'grey.200',
        border: 'none',
    },
}

/**
 * Icon-only button for compact actions where a text label isn't needed.
 *
 * Wraps C1Button with icon-specific padding, sizing, and hover color overrides.
 * Always requires an `aria-label` for accessibility since there's no visible text.
 *
 * ## Variants
 * - `text` — default. Minimal, borderless icon button. Text color starts as
 *   `text.secondary` and transitions to `primary.main` on hover. Use for inline
 *   actions where the icon meaning is obvious (edit, close, toggle).
 * - `outlined` — bordered icon button with full color support. Use when the icon
 *   needs visual emphasis or sits alongside other bordered elements (toolbars, table
 *   row actions).
 *
 * ## Colors
 * - **Text variant**: only `inherit` is available (renders as subdued `text.secondary`)
 * - **Outlined variant**: full `C1ButtonColorType` — `primary`, `error`, `success`,
 *   `info`, `inherit`. Hover states darken the border and text color for success/error.
 *
 * ## Sizes
 * - `small` (default) — 6-7px padding, standard icon size
 * - `medium` — 2-3px padding, larger touch target
 *
 * ## Behavior
 * - Clones the child element with `fontSize` set to the resolved `size` prop
 * - Disabled state removes the border and applies grey background/color
 * - Custom hover overrides for `outlinedSuccess`, `outlinedError`, and `textPrimary`
 *   are applied via MUI `buttonClasses` selectors
 *
 * @designSystem
 * @category atoms
 * @relatedComponent C1Button | Use when you need a text label alongside the action
 * @relatedComponent C1MoreButton | Use when you need an icon button that opens a dropdown menu
 * @externalReference MUI IconButton (concept) | https://mui.com/material-ui/api/icon-button/
 * @externalReference MUI Button API | https://mui.com/material-ui/api/button/
 */
export const C1IconButton: FC<C1IconButtonProps> = ({ children, size = 'small', sx, color = 'primary', variant = 'text', ...props }) => {
    const icon = React.cloneElement(children, { fontSize: size })
    const iconSx = {
        ...overridesSx,
        minWidth: 0,
        padding: size === 'medium'
            ? variant === 'outlined' ? '2px' : '3px'
            : variant === 'outlined' ? '6px' : '7px',
    }

    return (<C1Button sx={sxc(iconSx, sx)} variant={variant} color={color} {...props}>{icon}</C1Button>)
}
